import React, { useMemo } from "react"
import { Box, Tabs, Tab, TabList, TabPanels, TabPanel, Accordion, AccordionItem, AccordionButton, AccordionPanel } from "@chakra-ui/react"
import { graphql, useStaticQuery } from "gatsby"

import { useBundle } from "@app/hooks/useBundle"
import { useMedia } from "@app/hooks/useMedia"
import { useProductComputed } from "@app/hooks/useProductComputed"
import { useProductIngredients } from "@app/hooks/useProductIngredients"

import Icon from "@app/components/Icon"
import ProductTabDescription from "@app/components/Product/ProductTabs/ProductTabDescription"
import ProductTabBundle from "@app/components/Product/ProductTabs/ProductTabBundle"
import ProductTabGiftSet from "@app/components/Product/ProductTabs/ProductTabGiftSet"
import ProductTabIngredients from "@app/components/Product/ProductTabs/ProductTabIngredients"
import ProductTabInfo from "@app/components/Product/ProductTabs/ProductTabInfo"
import ProductTabHowToUse from "@app/components/Product/ProductTabs/ProductTabHowToUse"
import ProductTabReviews from "@app/components/Product/ProductTabs/ProductTabReviews"
import ProductTabProductHighlights from "@app/components/Product/ProductTabs/ProductTabProductHighlights"
import { ReviewDataResponseType } from "@root/types/reviews"

type Props = {
  product: GatsbyTypes.SanityProduct
  reviewData: ReviewDataResponseType | null
  reviewsRef: React.RefObject<HTMLDivElement>
}

const ProductTabs: React.FC<Props> = ({ product, reviewData, reviewsRef }) => {
  const { template } = useStaticQuery<GatsbyTypes.StaticTemplateProductQuery>(graphql`
    query StaticTemplateProduct {
      template: sanityTemplateProduct {
        productTabs: _rawProductTabs(resolveReferences: { maxDepth: 5 })
        tabDescription
        tabBundle
        tabGiftSets
        tabHowTo
        tabIngredients
        tabIngredientsSubTitle
        tabReviews
        settingEnableReviews
      }
    }
  `)

  const { productInfo } = useProductComputed(product)
  const { whatsInside } = useBundle(product)
  const { ingredients } = useProductIngredients(product)

  const { isLarge, isHydrated } = useMedia()

  const curatedReviews = useMemo(() => {
    const reviewsEnabled = template?.settingEnableReviews
    const reviews = reviewData && reviewData?.reviews?.filter(review => review.isCurated)

    return reviewsEnabled ? reviews || [] : []
  }, [reviewData, template?.settingEnableReviews])

  return (
    <>
      {(!isHydrated || isLarge) && (
        <Box maxWidth="1400px" px={{ base: "24px", sm: "56px" }} py={"48px"} w="100%" display={{ base: "none", lg: "block" }}>
          <Tabs isLazy>
            <TabList>
              {productInfo && template?.tabDescription && <Tab>{template.tabDescription}</Tab>}
              {whatsInside && <Tab>{template.tabBundle}</Tab>}
              {product?.giftSet && template?.tabGiftSets && <Tab>{template.tabGiftSets}</Tab>}
              {product?.howToUse && template?.tabHowTo && <Tab>{template.tabHowTo}</Tab>}
              {ingredients?.length && template?.tabIngredients && <Tab>{template.tabIngredients}</Tab>}
              {curatedReviews.length > 0 && <Tab>{template.tabReviews}</Tab>}
              {product?.productHighlights?.heading && <Tab>{product.productHighlights.heading}</Tab>}
              {template?.productTabs &&
                template?.productTabs.length > 0 &&
                template?.productTabs.map(tab => <Tab key={tab._key}>{tab.title}</Tab>)}
            </TabList>

            <TabPanels w="100%">
              {productInfo && (
                <TabPanel>
                  <ProductTabDescription productInfo={productInfo} productFragrences={product.fragrences} />
                </TabPanel>
              )}
              {whatsInside && (
                <TabPanel>
                  <ProductTabBundle whatsInside={whatsInside} />
                </TabPanel>
              )}
              {product?.giftSet && (
                <TabPanel>
                  <ProductTabGiftSet productGiftSet={product.giftSet} />
                </TabPanel>
              )}
              {product?.howToUse && (
                <TabPanel>
                  <ProductTabHowToUse productHowToUse={product.howToUse} />
                </TabPanel>
              )}
              {ingredients?.length > 0 && (
                <TabPanel>
                  <ProductTabIngredients
                    ingredients={ingredients}
                    title={template?.tabIngredients || ""}
                    subTitle={template?.tabIngredientsSubTitle || ""}
                  />
                </TabPanel>
              )}
              {curatedReviews.length > 0 && (
                <TabPanel>
                  <ProductTabReviews reviews={curatedReviews} reviewsRef={reviewsRef} />
                </TabPanel>
              )}
              {product?.productHighlights && (
                <TabPanel>
                  <ProductTabProductHighlights productHighlights={product.productHighlights} />
                </TabPanel>
              )}
              {template?.productTabs &&
                template?.productTabs.length > 0 &&
                template?.productTabs.map(tab => (
                  <TabPanel key={tab._key}>
                    <ProductTabInfo content={tab.content} />
                  </TabPanel>
                ))}
            </TabPanels>
          </Tabs>
        </Box>
      )}
      {(!isHydrated || !isLarge) && (
        <Box maxWidth="1400px" px={"24px"} py="32px" w="100%" display={{ base: "block", lg: "none" }}>
          <Accordion allowMultiple variant="pdp">
            {productInfo && (
              <AccordionItem>
                {({ isExpanded }) => (
                  <>
                    <AccordionButton>
                      <Icon
                        name={isExpanded ? "minus" : "plusProduct"}
                        width={isExpanded ? "14px" : "14px"}
                        height={isExpanded ? "3px" : "14px"}
                        color="black"
                      />
                      <Box as="span" flex="1" textAlign="left">
                        {template?.tabDescription}
                      </Box>
                    </AccordionButton>
                    <AccordionPanel pb={4}>
                      {isHydrated && <ProductTabDescription productInfo={productInfo} productFragrences={product.fragrences} />}
                    </AccordionPanel>
                  </>
                )}
              </AccordionItem>
            )}
            {whatsInside && (
              <AccordionItem>
                {({ isExpanded }) => (
                  <>
                    <AccordionButton>
                      <Icon
                        name={isExpanded ? "minus" : "plusProduct"}
                        width={isExpanded ? "14px" : "14px"}
                        height={isExpanded ? "3px" : "14px"}
                        color="black"
                      />
                      <Box as="span" flex="1" textAlign="left">
                        What&apos;s Inside
                      </Box>
                    </AccordionButton>
                    <AccordionPanel pb={4}>{isHydrated && <ProductTabBundle whatsInside={whatsInside} />}</AccordionPanel>
                  </>
                )}
              </AccordionItem>
            )}
            {product.giftSet && (
              <AccordionItem>
                {({ isExpanded }) => (
                  <>
                    <AccordionButton>
                      <Icon
                        name={isExpanded ? "minus" : "plusProduct"}
                        width={isExpanded ? "14px" : "14px"}
                        height={isExpanded ? "3px" : "14px"}
                        color="black"
                      />
                      <Box as="span" flex="1" textAlign="left">
                        {template?.tabGiftSets}
                      </Box>
                    </AccordionButton>
                    <AccordionPanel pb={4}>{isHydrated && <ProductTabGiftSet productGiftSet={product.giftSet} />}</AccordionPanel>
                  </>
                )}
              </AccordionItem>
            )}
            {product.howToUse && (
              <AccordionItem>
                {({ isExpanded }) => (
                  <>
                    <AccordionButton>
                      <Icon
                        name={isExpanded ? "minus" : "plusProduct"}
                        width={isExpanded ? "14px" : "14px"}
                        height={isExpanded ? "3px" : "14px"}
                        color="black"
                      />
                      <Box as="span" flex="1" textAlign="left">
                        {template?.tabHowTo}
                      </Box>
                    </AccordionButton>
                    <AccordionPanel pb={4}>{isHydrated && <ProductTabHowToUse productHowToUse={product.howToUse} />}</AccordionPanel>
                  </>
                )}
              </AccordionItem>
            )}
            {ingredients?.length && (
              <AccordionItem>
                {({ isExpanded }) => (
                  <>
                    <AccordionButton>
                      <Icon
                        name={isExpanded ? "minus" : "plusProduct"}
                        width={isExpanded ? "14px" : "14px"}
                        height={isExpanded ? "3px" : "14px"}
                        color="black"
                      />
                      <Box as="span" flex="1" textAlign="left">
                        {template?.tabIngredients}
                      </Box>
                    </AccordionButton>
                    <AccordionPanel pb={4}>
                      {isHydrated && (
                        <ProductTabIngredients
                          ingredients={ingredients}
                          title={template?.tabIngredients || ""}
                          subTitle={template?.tabIngredientsSubTitle || ""}
                        />
                      )}
                    </AccordionPanel>
                  </>
                )}
              </AccordionItem>
            )}
            {curatedReviews.length > 0 && (
              <AccordionItem>
                {({ isExpanded }) => (
                  <>
                    <AccordionButton>
                      <Icon
                        name={isExpanded ? "minus" : "plusProduct"}
                        width={isExpanded ? "14px" : "14px"}
                        height={isExpanded ? "3px" : "14px"}
                        color="black"
                      />
                      <Box as="span" flex="1" textAlign="left">
                        {template?.tabReviews}
                      </Box>
                    </AccordionButton>
                    <AccordionPanel pb={4}>
                      {isHydrated && <ProductTabReviews reviews={curatedReviews} reviewsRef={reviewsRef} />}
                    </AccordionPanel>
                  </>
                )}
              </AccordionItem>
            )}

            {product?.productHighlights && (
              <AccordionItem>
                {({ isExpanded }) => (
                  <>
                    <AccordionButton>
                      <Icon
                        name={isExpanded ? "minus" : "plusProduct"}
                        width={isExpanded ? "14px" : "14px"}
                        height={isExpanded ? "3px" : "14px"}
                        color="black"
                      />
                      <Box as="span" flex="1" textAlign="left">
                        {product.productHighlights?.heading}
                      </Box>
                    </AccordionButton>
                    <AccordionPanel pb={4}>
                      {isHydrated && <ProductTabProductHighlights productProductHighlights={product.productHighlights} />}
                    </AccordionPanel>
                  </>
                )}
              </AccordionItem>
            )}

            {template?.productTabs &&
              template?.productTabs.length > 0 &&
              template?.productTabs.map(tab => (
                <AccordionItem key={tab._key}>
                  {({ isExpanded }) => (
                    <>
                      <AccordionButton>
                        <Icon
                          name={isExpanded ? "minus" : "plusProduct"}
                          width={isExpanded ? "14px" : "14px"}
                          height={isExpanded ? "3px" : "14px"}
                          color="black"
                        />
                        <Box as="span" flex="1" textAlign="left">
                          {tab.title}
                        </Box>
                      </AccordionButton>
                      <AccordionPanel pb={4}>{isHydrated && <ProductTabInfo content={tab.content} />}</AccordionPanel>
                    </>
                  )}
                </AccordionItem>
              ))}
          </Accordion>
        </Box>
      )}
    </>
  )
}

export default React.memo(ProductTabs)
